import '../styles/event-list.scss';

/**
 * Reads data configuration attributes from the event list root.
 *
 * @param {HTMLElement} root
 * @returns {{
 *   endpoint: string,
 *   exhibitionId: number,
 *   taxonomyFilters: string,
 *   limit: number,
 * }}
 */
const readConfig = (root) => {
    const endpoint = root.getAttribute('data-endpoint') || '/wp-json/app/v1/events';
    const exhibitionId = parseInt(root.getAttribute('data-exhibition-id') || '0', 10);
    const taxonomyFilters = root.getAttribute('data-taxonomy-filters') || '';
    const limit = parseInt(root.getAttribute('data-limit') || '0', 10);

    return {
        endpoint,
        exhibitionId: Number.isNaN(exhibitionId) ? 0 : exhibitionId,
        taxonomyFilters,
        limit: Number.isNaN(limit) ? 0 : limit,
    };
};

/**
 * Collects active filter state (selected target groups and checked dates).
 *
 * @param {HTMLElement} root
 * @returns {{
 *   targetGroups: string[],
 *   dates: string[],
 * }}
 */
const readState = (root) => {
    const activeButtons = root.querySelectorAll('[data-target-group][aria-pressed="true"]');
    const targetGroups = Array.from(activeButtons)
        .map((button) => button.getAttribute('data-target-group'))
        .filter(Boolean);

    const checkedInputs = root.querySelectorAll('[data-event-list-date-checkbox]:checked');
    const dates = Array.from(checkedInputs)
        .map((input) => input.value)
        .filter(Boolean);

    return { targetGroups, dates };
};

/**
 * Builds the URLSearchParams query string for the REST API.
 * Uses config.limit (0 = unlimited matching events).
 *
 * @param {{
 *   endpoint: string,
 *   exhibitionId: number,
 *   taxonomyFilters: string,
 *   limit: number,
 * }} config
 * @param {{
 *   targetGroups: string[],
 *   dates: string[],
 * }} state
 * @returns {string}
 */
const buildQuery = (config, state) => {
    const params = new URLSearchParams();

    params.set('limit', String(config.limit));

    if (config.exhibitionId > 0) {
        params.set('exhibition_id', String(config.exhibitionId));
    }

    if (config.taxonomyFilters && config.taxonomyFilters !== '[]') {
        params.set('taxonomy_filters', config.taxonomyFilters);
    }

    if (state.targetGroups.length > 0) {
        params.set('target_groups', state.targetGroups.join(','));
    }

    if (state.dates.length > 0) {
        params.set('dates', state.dates.join(','));
    }

    return params.toString();
};

/**
 * Populates a single cloned event item element safely without innerHTML.
 *
 * @param {HTMLLIElement} item
 * @param {Record<string, any>} event
 */
const populateItem = (item, event) => {
    item.setAttribute('data-event-id', String(event.id || ''));
    item.setAttribute('data-date', String(event.date || ''));
    const categories = Array.isArray(event.targetGroupCategories)
        ? event.targetGroupCategories.join(' ')
        : '';
    item.setAttribute('data-target-groups', categories);

    // Media / Image
    const mediaContainer = item.querySelector('[data-event-media]');
    const img = item.querySelector('[data-event-image]');
    if (mediaContainer && img) {
        if (event.featuredImage && event.featuredImage.url) {
            img.setAttribute('src', event.featuredImage.url);
            img.setAttribute('alt', event.featuredImage.altText || '');
            img.removeAttribute('srcset');
            mediaContainer.removeAttribute('hidden');
        } else {
            mediaContainer.setAttribute('hidden', 'hidden');
            img.removeAttribute('src');
            img.removeAttribute('srcset');
            img.removeAttribute('alt');
        }
    }

    // Location badge
    const badge = item.querySelector('[data-event-badge]');
    const badgeText = item.querySelector('[data-event-badge-text]');
    if (badge) {
        if (event.hasLocationBadge && event.locationBadge) {
            if (badgeText) {
                badgeText.textContent = event.locationBadge;
            } else {
                badge.textContent = event.locationBadge;
            }
            badge.removeAttribute('hidden');
        } else {
            badge.remove();
        }
    }

    // Title / link
    const titleLink = item.querySelector('[data-event-link]');
    if (titleLink) {
        titleLink.textContent = event.title || '';
        titleLink.setAttribute('href', event.permalink || '#');
    }

    // Excerpt
    const excerpt = item.querySelector('[data-event-excerpt]');
    if (excerpt) {
        if (event.excerpt) {
            excerpt.textContent = event.excerpt;
            excerpt.removeAttribute('hidden');
        } else {
            excerpt.setAttribute('hidden', '');
            excerpt.textContent = '';
        }
    }

    // Metadata list
    const metaList = item.querySelector('[data-event-meta]');
    if (metaList) {
        metaList.replaceChildren();
        if (Array.isArray(event.metadata) && event.metadata.length > 0) {
            event.metadata.forEach((text) => {
                const li = document.createElement('li');
                li.className = 'f-event-list__item-meta-item';
                li.textContent = text;
                metaList.appendChild(li);
            });
            metaList.removeAttribute('hidden');
        } else {
            metaList.setAttribute('hidden', '');
        }
    }

    // CTA
    const ctaLink = item.querySelector('.f-event-list__item-cta .f-call-to-action__link');
    if (ctaLink) {
        ctaLink.setAttribute('href', event.permalink || '#');
    }
};

/**
 * Updates the date selector heading text based on active/checked date checkboxes.
 *
 * @param {HTMLElement} root
 */
const updateDateTitle = (root) => {
    const titleElement = root.querySelector('[data-event-list-title]');
    if (!titleElement) {
        return;
    }

    const checkedInputs = Array.from(root.querySelectorAll('[data-event-list-date-checkbox]:checked'));

    if (checkedInputs.length === 0) {
        const allUpcomingTitle = titleElement.getAttribute('data-all-upcoming-title')
            || titleElement.getAttribute('data-default-title')
            || '';
        titleElement.textContent = allUpcomingTitle;
        return;
    }

    if (checkedInputs.length === 1) {
        const title = checkedInputs[0].getAttribute('data-date-title')
            || checkedInputs[0].getAttribute('data-date-label')
            || checkedInputs[0].value;
        titleElement.textContent = title;
        return;
    }

    const titles = checkedInputs.map(
        (input) => input.getAttribute('data-date-label') || input.getAttribute('data-date-title') || input.value
    );
    titleElement.textContent = titles.join(', ');
};

/**
 * Renders the fetched events into the DOM using the template element.
 *
 * @param {HTMLElement} root
 * @param {Array<Record<string, any>>} events
 */
const renderEvents = (root, events) => {
    const itemsList = root.querySelector('[data-event-list-items]');
    const emptyNotice = root.querySelector('[data-event-list-empty]');
    const template = root.querySelector('[data-event-list-item-template]');

    if (!itemsList || !template) {
        return;
    }

    itemsList.replaceChildren();

    if (!events || events.length === 0) {
        itemsList.setAttribute('hidden', '');
        if (emptyNotice) {
            emptyNotice.removeAttribute('hidden');
        }
        return;
    }

    if (emptyNotice) {
        emptyNotice.setAttribute('hidden', '');
    }

    const fragment = document.createDocumentFragment();
    events.forEach((event) => {
        const clone = template.content.firstElementChild.cloneNode(true);
        populateItem(clone, event);
        fragment.appendChild(clone);
    });

    itemsList.appendChild(fragment);
    itemsList.removeAttribute('hidden');
};

/**
 * Initializes interactive filtering and REST fetching for an event list block.
 *
 * @param {HTMLElement} root
 */
const initEventList = (root) => {
    const config = readConfig(root);
    const body = root.querySelector('[data-event-list-body]');
    const itemsList = root.querySelector('[data-event-list-items]');
    const filterButtons = root.querySelectorAll('[data-target-group]');
    const dateToggle = root.querySelector('[data-event-list-date-toggle]');
    const dateDropdown = root.querySelector('[data-event-list-date-dropdown]');
    const dateSubmit = root.querySelector('[data-event-list-date-submit]');
    const resetBtn = root.querySelector('[data-event-list-reset]');
    const dateCheckboxes = root.querySelectorAll('[data-event-list-date-checkbox]');

    let currentController = null;
    let appliedDates = readState(root).dates;

    const setLoading = (loading) => {
        if (body) {
            body.classList.toggle('is-loading', loading);
        }
        if (itemsList) {
            itemsList.setAttribute('aria-busy', loading ? 'true' : 'false');
        }
    };

    const fetchAndRender = async () => {
        if (currentController) {
            currentController.abort();
        }
        const controller = new AbortController();
        currentController = controller;

        const state = readState(root);
        updateDateTitle(root);
        const query = buildQuery(config, state);
        const url = `${config.endpoint}?${query}`;

        setLoading(true);

        try {
            const response = await fetch(url, {
                signal: controller.signal,
                headers: {
                    Accept: 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Events fetch failed: ${response.status}`);
            }

            const data = await response.json();

            if (currentController !== controller) {
                return;
            }

            renderEvents(root, data.events || []);
            appliedDates = state.dates;
            updateDateTitle(root);
        } catch (error) {
            if (error.name !== 'AbortError') {
                // Keep existing items intact on failure, surface error to console
                console.error('Failed to load filtered events', error);
                if (currentController === controller) {
                    dateCheckboxes.forEach((checkbox) => {
                        checkbox.checked = appliedDates.includes(checkbox.value);
                    });
                    updateDateTitle(root);
                }
            }
        } finally {
            if (currentController === controller) {
                setLoading(false);
            }
        }
    };

    let debounceTimer = null;
    const debouncedFetch = (delay = 200) => {
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
        debounceTimer = setTimeout(() => {
            fetchAndRender();
        }, delay);
    };

    // Target group filter buttons (multi-select)
    filterButtons.forEach((button) => {
        button.addEventListener('click', () => {
            const isPressed = button.getAttribute('aria-pressed') === 'true';
            button.setAttribute('aria-pressed', isPressed ? 'false' : 'true');
            button.classList.toggle('is-active', !isPressed);
            debouncedFetch();
        });
    });

    // Date checkboxes change handler
    dateCheckboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', () => {
            updateDateTitle(root);
        });
    });

    // Date dropdown toggle
    if (dateToggle && dateDropdown) {
        const closeAndRevertDropdown = (focusToggle = false) => {
            dateDropdown.setAttribute('hidden', '');
            dateToggle.setAttribute('aria-expanded', 'false');
            if (focusToggle) {
                dateToggle.focus();
            }
            dateCheckboxes.forEach((checkbox) => {
                checkbox.checked = appliedDates.includes(checkbox.value);
            });
            updateDateTitle(root);
        };

        dateToggle.addEventListener('click', (event) => {
            event.stopPropagation();
            const isOpen = !dateDropdown.hasAttribute('hidden');
            if (isOpen) {
                closeAndRevertDropdown();
            } else {
                dateDropdown.removeAttribute('hidden');
                dateToggle.setAttribute('aria-expanded', 'true');
            }
        });

        document.addEventListener('click', (event) => {
            if (!dateDropdown.hasAttribute('hidden') && !dateDropdown.contains(event.target)) {
                closeAndRevertDropdown();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !dateDropdown.hasAttribute('hidden')) {
                closeAndRevertDropdown(true);
            }
        });
    }

    // Date dropdown submit button
    if (dateSubmit) {
        dateSubmit.addEventListener('click', () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }

            if (dateDropdown && dateToggle) {
                dateDropdown.setAttribute('hidden', '');
                dateToggle.setAttribute('aria-expanded', 'false');
            }
            fetchAndRender();
        });
    }

    // Reset button: clear target groups, uncheck non-default dates, and refetch
    if (resetBtn) {
        resetBtn.addEventListener('click', () => {
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }

            filterButtons.forEach((button) => {
                button.setAttribute('aria-pressed', 'false');
                button.classList.remove('is-active');
            });

            dateCheckboxes.forEach((checkbox) => {
                checkbox.checked = checkbox.defaultChecked;
            });

            if (dateDropdown && dateToggle) {
                dateDropdown.setAttribute('hidden', '');
                dateToggle.setAttribute('aria-expanded', 'false');
            }

            fetchAndRender();
        });
    }
};

document.addEventListener('DOMContentLoaded', () => {
    const roots = document.querySelectorAll('[data-event-list]');
    roots.forEach((root) => initEventList(root));
});
